import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  Typography,
  Button,
  Select,
  MenuItem,
  FormControl,
  TextField,
  Box,
  Divider,
  CircularProgress,
} from "@mui/material";
import { LoadingButton } from "@mui/lab";
import { useDispatch, useSelector } from "react-redux";
import CloseIcon from "@mui/icons-material/Close";
import {
  failCoaFormUnderQualityControl,
  selectIsFailing,
  selectCoaFormFailResponse,
  clearCoaFormFailResponse,
  getTestList,
  selectTestList,
  selectIsLoadingTestList,
} from "../../slices/qualityControl";
import { notify } from "../../utils/methods/notification";
import { selectAllInitialSteps,selectCurrentCycle } from "../../slices/common";
import { rejectQualityApproval } from "../../slices/qualityApproval";
// import { dateFormater } from "../../utils/common/utils";

const FailedProductModal = ({
  open,
  onClose,
  patientData,
  feasibilityId,
  row,
  coaFormId,
  fetchPatientInfo,
  updateTimeLine
}) => {
  const dispatch = useDispatch();
  const allInitialSteps = useSelector(selectAllInitialSteps)
  const approvalStatus = allInitialSteps?.find(op => op.slug == "qc-testing")
  const [reasons, setReasons] = useState([{ id: 1, reason: "", comment: "" }]);
  // Redux selectors
  const isFailing = useSelector(selectIsFailing);
  const failResponse = useSelector(selectCoaFormFailResponse);
  const testList = useSelector(selectTestList);
  const isLoadingTestList = useSelector(selectIsLoadingTestList);
  const currentCycle = useSelector(selectCurrentCycle)

  // Fetch test list when modal opens
  useEffect(() => {
    if (open) {
      dispatch(getTestList());
    }
  }, [open, dispatch]);

  const handleReasonChange = (id, field, value) => {
    setReasons((prev) =>
      prev.map((item) => (item.id === id ? { ...item, [field]: value } : item))
    );
  };

  const handleAddReason = () => {
    const newId = Math.max(...reasons.map((r) => r.id)) + 1;
    setReasons((prev) => [...prev, { id: newId, reason: "", comment: "" }]);
  };

  const handleRemoveReason = (id) => {
    if (reasons.length > 1) {
      setReasons((prev) => prev.filter((item) => item.id !== id));
    }
  };



  const handleFailProduct = async () => {
    const validReasons = reasons.filter((r) => r.reason && r.comment);
    if (validReasons.length > 0) {
      try {
        const failureReasons = validReasons.map((reason) => ({
          reason: reason.reason,
          comment: reason.comment,
        }));

        let apiPayload;

        if (approvalStatus?.status === 2) {
          // Case 1 — only send failure_reasons + coa_form_id
          apiPayload = {
            failure_reasons: failureReasons,
            coa_form_id: coaFormId,
            feasibility_assesment_id: feasibilityId,
            product_manufacturing_id:currentCycle?.product_manufacturing_id ||
              row?.product_manufacturing_id ||
              patientData?.product_manufacturing_id ||
              "",
            patient_id:
              row?.subject_id || patientData?.patient_information?.id || "",
            qr_code_generated_id: row?.qr_code_generated_id || patientData?.id,
          };
          await dispatch(
            failCoaFormUnderQualityControl({ formData: apiPayload, id: coaFormId })
          ).unwrap();
        } else {
          apiPayload = {
            failure_reasons: failureReasons,
            patient_id:
              row?.subject_id || patientData?.patient_information?.id || "",
            feasibility_assesment_id: feasibilityId,
            product_manufacturing_id: currentCycle?.product_manufacturing_id || 
              row?.product_manufacturing_id ||
              patientData?.product_manufacturing_id ||
              "",
            qr_code_generated_id:
              row?.qr_code_generated_id || patientData?.id || "",
          };
          await dispatch(
            rejectQualityApproval({ formData: apiPayload, id: coaFormId })
          ).unwrap();


        }


        await updateTimeLine()
        await fetchPatientInfo()

        notify.success("Product failed successfully!");
      } catch (error) {
        console.error("Error failing COA form:", error);
      }
    }
  };

  const handleClose = () => {
    setReasons([{ id: 1, reason: "", comment: "" }]);
    onClose();
  };

  // Handle successful failure submission
  useEffect(() => {
    if (failResponse) {
      // Close modal on success
      handleClose();
      // Clear the response after handling
      setTimeout(() => {
        dispatch(clearCoaFormFailResponse());
      }, 3000);
    }
  }, [failResponse, dispatch]);

  // Clear response when component unmounts
  useEffect(() => {
    return () => {
      dispatch(clearCoaFormFailResponse());
    };
  }, [dispatch]);

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: "0.5rem",
          minHeight: "25rem",
          maxWidth: "34.375rem",
          width: "100%",
        },
      }}
    >
      <DialogTitle sx={{ p: 0, position: "relative" }}>
        <Box className="flex justify-center items-center p-3 pb-2">
          <Typography variant="h6" className="font-semibold text-xl">
            Failed Product
          </Typography>
          <IconButton
            onClick={handleClose}
            size="small"
            sx={{ position: "absolute", top: "1rem", right: "1rem" }}
            disabled={isFailing}
          >
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>
      <Divider />

      <DialogContent sx={{ px: "1rem", pb: "1rem" }}>
        <Box className="space-y-4">
          {isLoadingTestList && (
            <Box className="flex justify-center items-center py-4">
              <CircularProgress size={24} />
              <Typography sx={{ ml: 2 }}>Loading test list...</Typography>
            </Box>
          )}

          {reasons.map((item, index) => (
            <Box
              key={item.id}
              className={`${reasons.length > 1
                ? "border-2 border-dashed border-blue-300 rounded-lg p-4"
                : ""
                } relative`}
            >
              {reasons.length > 1 && (
                <IconButton
                  onClick={() => handleRemoveReason(item.id)}
                  size="small"
                  className="absolute top-2 right-2"
                  sx={{ color: "#666" }}
                  disabled={isFailing}
                >
                  <CloseIcon fontSize="small" />
                </IconButton>
              )}

              <Box className="space-y-4">
                <Box>
                  <Typography className="text-sm font-small mb-2">
                    {index + 1}. Select a test to fail
                    <span className="text-red-500 ml-1">*</span>
                  </Typography>
                  <FormControl fullWidth size="small">
                    <Select
                      value={item.reason}
                      onChange={(e) =>
                        handleReasonChange(item.id, "reason", e.target.value)
                      }
                      displayEmpty
                      disabled={isFailing || isLoadingTestList}
                    >
                      <MenuItem value="" disabled>
                        {isLoadingTestList ? "Loading..." : "Select Test"}
                      </MenuItem>
                      {(testList || []).map((test) => (
                        <MenuItem key={test.id} value={test.id}>
                          {test.name}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Box>

                <Box>
                  <Typography className="text-sm font-small mb-2">
                    Comment
                    <span className="text-red-500 ml-1">*</span>
                  </Typography>
                  <TextField
                    fullWidth
                    multiline
                    rows={3}
                    value={item.comment}
                    onChange={(e) =>
                      handleReasonChange(item.id, "comment", e.target.value)
                    }
                    placeholder="Add Comment"
                    variant="outlined"
                    size="small"
                    disabled={isFailing}
                  />
                </Box>
              </Box>
            </Box>
          ))}

          <Button
            variant="text"
            onClick={handleAddReason}
            disabled={isFailing || isLoadingTestList}
            sx={{
              color: "#2563eb",
              border: "1px solid #2563eb",
              backgroundColor: "white",
              textTransform: "none",
              fontSize: "0.875rem",
              fontWeight: "500",
              padding: "0.5rem 1rem",
              borderRadius: "0.25rem",
              "&:hover": {
                backgroundColor: "#eff6ff",
                border: "1px solid #1d4ed8",
              },
            }}
          >
            Add Reason
          </Button>
        </Box>

        <Box className="flex justify-center mt-6">
          <LoadingButton
            fullWidth
            variant="contained"
            onClick={handleFailProduct}
            loading={isFailing}
            disabled={
              reasons.some((r) => !r.reason || !r.comment) ||
              isFailing ||
              isLoadingTestList
            }
            sx={{
              border: "1px solid rgb(235, 38, 81)",
              color: "#dc2626",
              backgroundColor: "white",
              textTransform: "none",
              fontSize: "0.875rem",
              fontWeight: "500",
              padding: "0.5rem 1rem",
              borderRadius: "0.25rem",
              minWidth: "12.5rem",
              "&:hover": {
                backgroundColor: "rgb(255, 246, 246)",
              },
              "&:disabled": {
                backgroundColor: "#d1d5db",
                color: "#9ca3af",
              },
            }}
          >
            {isFailing ? "Failing Product..." : "Fail Product"}
          </LoadingButton>
        </Box>
      </DialogContent>
    </Dialog>
  );
};

export default FailedProductModal;
